const PLACEHOLDER_PATTERN = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;
const OPEN_PLACEHOLDER_PATTERN = /\{\{/g;
const LEADING_PLACEHOLDER = /^\{\{\s*[a-z0-9_]+\s*\}\}/;
const TRAILING_PLACEHOLDER = /\{\{\s*[a-z0-9_]+\s*\}\}$/;

export const MAX_HEADER_LENGTH = 60;
export const MAX_BODY_LENGTH = 1024;
export const MAX_FOOTER_LENGTH = 60;
export const MAX_BUTTONS = 3;
export const MAX_BUTTON_TEXT_LENGTH = 25;

export const WHATSAPP_MANAGER_URL =
  "https://business.facebook.com/wa/manage/message-templates/";

export const LANGUAGE_OPTIONS = [
  { value: "es_AR", label: "Español (Argentina)" },
  { value: "es", label: "Español" },
  { value: "es_MX", label: "Español (México)" },
  { value: "es_ES", label: "Español (España)" },
  { value: "en_US", label: "Inglés (EE. UU.)" },
  { value: "pt_BR", label: "Portugués (Brasil)" },
];

export const CATEGORY_OPTIONS = [
  {
    value: "UTILITY",
    label: "Utilidad",
    description:
      "Seguimientos de algo que el contacto pidió o ya está en curso: una cotización, un pedido, una reunión.",
  },
  {
    value: "MARKETING",
    label: "Marketing",
    description:
      "Promociones, novedades o primer contacto comercial. Meta la cobra más cara y la revisa con más rigor.",
  },
];

export const TEMPLATE_STATUS_LABELS = {
  APPROVED: { label: "Aprobada", tone: "success" },
  PENDING: { label: "En revisión", tone: "pending" },
  IN_APPEAL: { label: "En apelación", tone: "pending" },
  REJECTED: { label: "Rechazada", tone: "danger" },
  PAUSED: { label: "Pausada", tone: "danger" },
  DISABLED: { label: "Deshabilitada", tone: "danger" },
  PENDING_DELETION: { label: "Borrándose", tone: "pending" },
  FLAGGED: { label: "Baja calidad", tone: "danger" },
};

export const statusInfo = (status) =>
  TEMPLATE_STATUS_LABELS[(status || "").toUpperCase()] || {
    label: status || "Sin estado",
    tone: "pending",
  };

export const emptyDraft = () => ({
  name: "",
  language: "es_AR",
  category: "UTILITY",
  headerText: "",
  bodyText: "",
  footerText: "",
  examples: {},
  buttons: [],
});

export const sanitizeTemplateName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 128);

export const variableNames = (text) => {
  const found = [...new Set([...String(text || "").matchAll(PLACEHOLDER_PATTERN)].map((match) => match[1]))];
  if (found.length && found.every((name) => /^\d+$/.test(name))) {
    return [...found].sort((left, right) => Number(left) - Number(right));
  }
  return found;
};

export const insertAt = (text, position, snippet) => {
  const source = String(text || "");
  const index = position == null ? source.length : Math.max(0, Math.min(position, source.length));
  const before = source.slice(0, index);
  const after = source.slice(index);
  const leading = before && !/\s$/.test(before) ? " " : "";
  const trailing = after && !/^[\s.,;:!?]/.test(after) ? " " : "";
  return { text: `${before}${leading}${snippet}${trailing}${after}`, cursor: index + leading.length + snippet.length };
};

const kindOf = (names) => {
  if (!names.length) return null;
  return names.every((name) => /^\d+$/.test(name)) ? "numbered" : "named";
};

const checkVariables = (text, examples, { field, maxVariables = null, checkEdges = false }) => {
  const source = String(text || "");
  const opened = (source.match(OPEN_PLACEHOLDER_PATTERN) || []).length;
  const valid = [...source.matchAll(PLACEHOLDER_PATTERN)].length;
  if (opened !== valid) {
    return { [field]: "Los tags van entre llaves dobles, en minúsculas y con guiones bajos: {{first_name}}." };
  }
  const names = variableNames(source);
  const numbered = names.filter((name) => /^\d+$/.test(name));
  if (numbered.length && numbered.length !== names.length) {
    return { [field]: "No mezcles variables numeradas ({{1}}) con tags ({{first_name}})." };
  }
  if (numbered.length && numbered.some((name, index) => name !== String(index + 1))) {
    return { [field]: "Las variables tienen que ir en orden: {{1}}, {{2}}, {{3}}..." };
  }
  if (maxVariables != null && names.length > maxVariables) {
    return { [field]: `Admite como máximo ${maxVariables} tag.` };
  }
  const trimmed = source.trim();
  if (checkEdges && names.length && (LEADING_PLACEHOLDER.test(trimmed) || TRAILING_PLACEHOLDER.test(trimmed))) {
    return { [field]: "El texto no puede empezar ni terminar con un tag." };
  }
  if (names.some((name) => !String(examples[name] ?? "").trim())) {
    return { [`${field}Examples`]: "Completá un ejemplo para cada tag: Meta lo usa para revisar la plantilla." };
  }
  return {};
};

export const validateDraft = (draft) => {
  const errors = {};
  if (!draft.name) {
    errors.name = "Poné un nombre para identificar la plantilla.";
  } else if (!/^[a-z0-9_]+$/.test(draft.name)) {
    errors.name = "Solo minúsculas, números y guiones bajos.";
  }
  const body = draft.bodyText.trim();
  if (!body) {
    errors.bodyText = "Escribí el mensaje.";
  } else if (body.length > MAX_BODY_LENGTH) {
    errors.bodyText = `Admite hasta ${MAX_BODY_LENGTH} caracteres.`;
  } else {
    Object.assign(errors, checkVariables(body, draft.examples, { field: "bodyText", checkEdges: true }));
  }
  const header = draft.headerText.trim();
  if (header.length > MAX_HEADER_LENGTH) {
    errors.headerText = `Admite hasta ${MAX_HEADER_LENGTH} caracteres.`;
  } else if (header) {
    Object.assign(errors, checkVariables(header, draft.examples, { field: "headerText", maxVariables: 1 }));
  }
  const headerKind = kindOf(variableNames(header));
  const bodyKind = kindOf(variableNames(body));
  if (!errors.headerText && headerKind && bodyKind && headerKind !== bodyKind) {
    errors.headerText = "Usá tags en el encabezado y en el mensaje, sin mezclar con variables numeradas.";
  }
  const footer = draft.footerText.trim();
  if (footer.length > MAX_FOOTER_LENGTH) {
    errors.footerText = `Admite hasta ${MAX_FOOTER_LENGTH} caracteres.`;
  } else if (/\{\{/.test(footer)) {
    errors.footerText = "El pie no admite tags.";
  }
  draft.buttons.forEach((button) => {
    if (!button.text.trim()) {
      errors.buttons = "Cada botón necesita un texto.";
    } else if (button.type === "URL" && !button.url.trim().startsWith("https://")) {
      errors.buttons = "El link del botón tiene que empezar con https://.";
    } else if (button.type === "URL" && /\{\{/.test(button.url)) {
      errors.buttons = "Los links con tags todavía no se pueden crear desde acá.";
    }
  });
  return errors;
};

const examplesFor = (text, examples) =>
  variableNames(text).map((name) => String(examples[name] ?? "").trim());

export const buildTemplatePayload = (draft) => ({
  name: draft.name,
  language: draft.language,
  category: draft.category,
  header_text: draft.headerText.trim(),
  header_examples: examplesFor(draft.headerText, draft.examples),
  body_text: draft.bodyText.trim(),
  body_examples: examplesFor(draft.bodyText, draft.examples),
  footer_text: draft.footerText.trim(),
  buttons: draft.buttons.map((button) => ({
    type: button.type,
    text: button.text.trim(),
    ...(button.type === "URL" ? { url: button.url.trim() } : {}),
  })),
});

const BLOCK_TAGS = new Set(["P", "DIV", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE"]);
const INLINE_MARKS = { STRONG: "*", B: "*", EM: "_", I: "_", S: "~", STRIKE: "~", DEL: "~" };

const nodeToWhatsAppText = (node, formatting) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent.replace(/\u00a0/g, " ").replace(/\ufeff/g, "");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  if (node.tagName === "BR") return "\n";
  if (node.hasAttribute("data-template-key")) return `{{${node.getAttribute("data-template-key")}}}`;
  const inner = Array.from(node.childNodes)
    .map((child) => nodeToWhatsAppText(child, formatting))
    .join("");
  const mark = formatting ? INLINE_MARKS[node.tagName] : null;
  if (mark && inner.trim()) {
    const leading = inner.match(/^\s*/)[0];
    const trailing = inner.match(/\s*$/)[0];
    return `${leading}${mark}${inner.trim()}${mark}${trailing}`;
  }
  return BLOCK_TAGS.has(node.tagName) ? `${inner}\n` : inner;
};

export const htmlToWhatsAppText = (html, { formatting = true } = {}) => {
  const source = String(html || "");
  if (!source) return "";
  if (typeof DOMParser === "undefined") return source;
  const { body } = new DOMParser().parseFromString(source, "text/html");
  return Array.from(body.childNodes)
    .map((node) => nodeToWhatsAppText(node, formatting))
    .join("")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const TAG_TOKEN_PATTERN = /\{\{[^{}]+\}\}/g;
const TAG_PLACEHOLDER_START = "";
const TAG_PLACEHOLDER_END = "";
const TAG_PLACEHOLDER_PATTERN = new RegExp(`${TAG_PLACEHOLDER_START}(\\d+)${TAG_PLACEHOLDER_END}`, "g");
const WHATSAPP_INLINE_MARKS = [
  [/\*([^*\n]+)\*/g, "strong"],
  [/_([^_\n]+)_/g, "em"],
  [/~([^~\n]+)~/g, "s"],
];

const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const whatsAppLineToHtml = (line) => {
  const tagTokens = [];
  const protectedLine = line.replace(TAG_TOKEN_PATTERN, (token) => {
    tagTokens.push(token);
    return `${TAG_PLACEHOLDER_START}${tagTokens.length - 1}${TAG_PLACEHOLDER_END}`;
  });
  const formatted = WHATSAPP_INLINE_MARKS.reduce(
    (html, [pattern, tag]) => html.replace(pattern, `<${tag}>$1</${tag}>`),
    escapeHtml(protectedLine),
  );
  return formatted.replace(TAG_PLACEHOLDER_PATTERN, (_, index) => tagTokens[Number(index)]);
};

export const whatsAppTextToHtml = (text) => {
  const source = String(text || "");
  if (!source.trim()) return "";
  return source
    .split("\n")
    .map((line) => `<p>${whatsAppLineToHtml(line) || "<br>"}</p>`)
    .join("");
};

export const draftFromTemplate = (template) => ({
  ...emptyDraft(),
  name: template.name || "",
  language: template.language || emptyDraft().language,
  category: (template.category || emptyDraft().category).toUpperCase(),
  headerText: template.header_text || "",
  bodyText: template.body_text || "",
  footerText: template.footer_text || "",
  buttons: (template.buttons || []).map((button) => ({
    type: (button.type || "QUICK_REPLY").toUpperCase(),
    text: button.text || "",
    url: button.url || "",
  })),
});

export const fillExamples = (text, examples = {}) =>
  String(text || "").replace(PLACEHOLDER_PATTERN, (match, name) => {
    const value = examples[name];
    return value && String(value).trim() ? String(value).trim() : match;
  });
